import React, { useCallback, useMemo, useState } from 'react';
import { Box, DownloadIcon, List, PDFIcon, PlusMoneyIcon, RawButton } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import ExcessContributionWithdrawModal from '../ExcessContributionWithdrawModal/ExcessContributionWithdrawModal';

import { useDownloadTaxFile, useTaxFormHighlight } from './hooks';
import { ITaxForm, ITaxFormType, ITaxModalPayload } from './TaxForms.types';
import TaxFormsMessageQueue from './TaxFormsMessageQueue';

interface Props extends ITaxForm {
  isActiveHSAExists?: boolean;
  onOpenMailingQueue?: (value: ITaxModalPayload) => void;
}

const ONLY_YEAR_KEY = 'YYYY';
const PREVIOUS_TAX_YEAR = dayjs().add(-1, 'year').format(ONLY_YEAR_KEY);

const TaxFormsItem: React.FC<Props> = ({
  id,
  name,
  type,
  isActiveHSAExists,
  history,
  onOpenMailingQueue,
  taxYear,
  ...rest
}) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const isTaxFormExcess = useMemo(() => type === ITaxFormType.TAX_FORM_EXCESS_1099, [type]);

  const { itemRef, isVisualHighlightActive } = useTaxFormHighlight(id);

  const { handleDownload, handleOpenPreview } = useDownloadTaxFile({
    id,
    name,
    type,
    history,
    taxYear,
    ...rest,
  });
  const handleViewExcessContribution = useCallback(() => setModalVisible(true), [setModalVisible]);
  const handleOpenMailingModal = () => {
    if (onOpenMailingQueue) {
      onOpenMailingQueue({
        id,
        type: type,
      });
    }
  };
  const isMailingAvailable = useMemo(() => {
    if (type !== ITaxFormType.TAX_FORM_1099 && type !== ITaxFormType.TAX_FORM_5498) {
      return false;
    }
    return !taxYear || taxYear === PREVIOUS_TAX_YEAR;
  }, [taxYear, type]);
  return (
    <>
      {isModalVisible ? <ExcessContributionWithdrawModal onSetVisible={setModalVisible} id={id} /> : null}
      <List.Item ref={itemRef} testId={`tax-form_item_${id}`} isHighlighted={isVisualHighlightActive}>
        {isTaxFormExcess ? (
          <Box
            width="32px"
            height="32px"
            background="nonClaimBg"
            align="center"
            justify="center"
            round="fieldRound"
          >
            <PlusMoneyIcon color="nonClaimIcon" />
          </Box>
        ) : (
          <Box
            pad="xxs"
            data-testId={`tax-form_item_${id}_pdf-icon`}
            round="fieldRound"
            border={{ size: 'small', color: 'border1' }}
          >
            <PDFIcon />
          </Box>
        )}
        <List.Item.Link
          testId={`tax-form_item_${id}_name`}
          margin={{ left: 's' }}
          onClick={isTaxFormExcess && isActiveHSAExists ? handleViewExcessContribution : handleOpenPreview}
        >
          {name}
        </List.Item.Link>
        {isMailingAvailable && <TaxFormsMessageQueue items={history} />}

        {isTaxFormExcess ? null : (
          <RawButton data-testId={`tax-form_item_${id}_download-icon`} onClick={handleDownload}>
            <DownloadIcon color="iconPrimary" />
          </RawButton>
        )}
        <Box margin={{ left: 's' }}>
          <List.Item.Options testId={`tax-form_item_${id}_options`}>
            <List.Item.Options.Item
              testId={`tax-form_item_${id}_options_view`}
              onClick={
                isTaxFormExcess && isActiveHSAExists ? handleViewExcessContribution : handleOpenPreview
              }
            >
              View
            </List.Item.Options.Item>
            {isTaxFormExcess ? null : (
              <List.Item.Options.Item
                testId={`tax-form_item_${id}_options_download`}
                onClick={handleDownload}
              >
                Download
              </List.Item.Options.Item>
            )}
            {!!onOpenMailingQueue && !!isMailingAvailable && (
              <List.Item.Options.Item
                testId={`tax-form_item_${id}_options_queue-for-mail`}
                onClick={() => handleOpenMailingModal()}
              >
                Queue for mail
              </List.Item.Options.Item>
            )}
          </List.Item.Options>
        </Box>
      </List.Item>
    </>
  );
};

export default TaxFormsItem;
